import React, {useState, useEffect, useMemo, useRef} from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Box,
    FocusLock,
    HStack,
    Link,
    LinkProps,
    List,
    ListItem,
    ListProps,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverContentProps,
    PopoverProps,
    PopoverTrigger,
    Portal,
    StackProps,
    Text,
    TextProps,
    useBreakpointValue,
    useClipboard,
    useDisclosure,
    useMultiStyleConfig,
    VStack,
} from '@chakra-ui/react'
import {RemoveScroll} from 'react-remove-scroll'
import {QRCodeSVG as QRCode} from 'qrcode.react'
import {Icon, IconButton, DarkOverlay} from '_uiComponents'
import {useAnalytics} from '_customHooks'
import type {ShareSocialName, ShareSocial, WishlistShareSocialAction} from '_types'
import {useGlobalConfigState} from '_context'

interface ShareBoxProps {
    isOpen: boolean
    onClose: () => void
    shareUrl: string
    shareSubject?: string
    children: React.ReactNode
    anchorNearEdge?: boolean
}

/**
 * @name ShareBox
 *
 * @component
 *
 * @description
 * Renders the content of share popover in the wishlist page.
 *
 * @remarks
 * - This popover is rendered on `share wishlist` and `share product` button click.
 * - List of socials is retrieved via custom preference except for e-mail which is static.
 * - `PopoverContent` is wrapped in a `Portal` to support correct `z-index` positioning for `DarkOverlay` rendering.
 * - Mobile responsive is provided via a different render content of the component.
 *
 * @param {ShareBoxProps} props - Properties object.
 * @param {boolean} props.isOpen - Indicates if the popover body is opened or not.
 * @param {function} props.onClose - Specifies the behaviour for popover close.
 * @param {string} props.shareUrl - URL that will be shared.
 * @param {string | undefined} props.shareSubject - Subject of share that will be used for mail subject value.
 * @param {React.ReactNode} props.children - Popover trigger content.
 * @param {boolean | undefined} props.anchorNearEdge - When true, anchors the popover to the trigger's end edge
 * with extra shifting padding, for triggers placed close to the window edge (e.g. the zoom modal header).
 *
 * @returns {React.FC<ShareBoxProps>} - Rendered `ShareBox` component.
 */
const ShareBox: React.FC<ShareBoxProps> = ({isOpen, onClose, shareUrl, shareSubject = '', children, anchorNearEdge = false}) => {
    const styles = useMultiStyleConfig('ShareBox') as Record<string, StackProps & PopoverContentProps & PopoverProps & TextProps & LinkProps & ListProps>
    const {formatMessage} = useIntl()
    const {analyticsEventWishlistShare} = useAnalytics()
    const isMobile = useBreakpointValue({base: true, md: false}) as boolean
    const {appPreferences} = useGlobalConfigState()
    const {enableSocialShare: data} = appPreferences
    const {isOpen: showQRCode, onOpen: onQRCodeShow, onClose: onQRCodeHide} = useDisclosure()

    const shareBoxMobile = useRef<HTMLElement>()
    const closeButtonDesktop = useRef<HTMLButtonElement | null>(null)

    const socialsShareData = ['email', ...(data ?? [])] as Array<ShareSocialName>

    const {onCopy} = useClipboard(shareUrl)
    const [isLinkCopied, setIsLinkCopied] = useState(false)
    const copyLinkLabel = useMemo(
        () =>
            !isLinkCopied
                ? formatMessage({id: 'label.wishlist.copy.link', defaultMessage: 'Copy link'})
                : formatMessage({id: 'label.wishlist.copied.link', defaultMessage: 'Link copied'}),
        [isLinkCopied],
    )

    const mailContent = {
        subject: encodeURIComponent(shareSubject ?? ''),
        body: encodeURIComponent(shareUrl),
    }
    const mailToUrl = `mailto:?subject=${mailContent.subject}&body=${mailContent.body}`

    const socialsShare = new Map<ShareSocialName, ShareSocial>([
        [
            'email',
            {
                socialAction: 'mail',
                iconName: 'IconEmailShare',
                href: mailToUrl,
                ariaLabel: formatMessage({id: 'label.socialshare.email', defaultMessage: 'Email'}),
            },
        ],
        [
            'whatsapp',
            {
                socialAction: 'share',
                iconName: 'IconWhatsapp',
                href: `https://wa.me/?text=${shareUrl}`,
                ariaLabel: formatMessage({id: 'label.socialshare.whatsapp', defaultMessage: 'Whatsapp'}),
            },
        ],
        [
            'pinterest',
            {
                socialAction: 'pin',
                iconName: 'IconPinterestShare',
                href: `https://www.pinterest.com/pin/create/button/?url=${shareUrl}`,
                ariaLabel: formatMessage({id: 'label.socialshare.pinterest', defaultMessage: 'Pinterest'}),
            },
        ],
        [
            'facebook',
            {
                socialAction: 'share',
                iconName: 'IconFacebookShare',
                href: `https://www.facebook.com/share.php?u=${shareUrl}`,
                ariaLabel: formatMessage({id: 'label.socialshare.facebook', defaultMessage: 'Facebook'}),
            },
        ],
        [
            'twitter',
            {
                socialAction: 'share',
                iconName: 'IconX',
                href: `https://twitter.com/share?url=${shareUrl}`,
                ariaLabel: formatMessage({id: 'label.socialshare.twitter', defaultMessage: 'X'}),
                mobileIconSize: 'share-mobile',
            },
        ],
        ['wechat', {socialAction: 'share', iconName: 'IconWeChatShare', ariaLabel: formatMessage({id: 'label.socialshare.wechat', defaultMessage: 'Wechat'})}],
        [
            'weibo',
            {
                socialAction: 'share',
                iconName: 'IconWeiboShare',
                href: `http://service.weibo.com/share/share.php?url=${shareUrl}`,
                ariaLabel: formatMessage({id: 'label.socialshare.weibo', defaultMessage: 'Weibo'}),
            },
        ],
    ])

    const handleCopyLink = () => {
        analyticsEventWishlistShare({
            socialNetwork: 'Link' as ShareSocialName,
            socialAction: 'share',
            socialTarget: shareUrl,
        })
        onCopy()
        setIsLinkCopied(true)
    }

    const handleSocialShare = (event: React.MouseEvent<HTMLAnchorElement>, socialName: ShareSocialName, socialAction: WishlistShareSocialAction) => {
        if (socialName !== 'email') {
            event.preventDefault()

            const href = event.currentTarget.getAttribute('href')

            if (href) {
                window.open(href, '_blank', 'location=yes,height=570,width=520,scrollbars=yes,status=yes')
            }
        }

        analyticsEventWishlistShare({
            socialNetwork: socialName,
            socialAction: socialAction,
            socialTarget: shareUrl,
        })
    }

    const handleWeChatSocialClick = () => {
        if (!showQRCode) {
            onQRCodeShow()
        } else {
            onQRCodeHide()
        }
    }

    useEffect(() => {
        if (isLinkCopied) {
            const timeout = setTimeout(() => setIsLinkCopied(false), 3000)
            return () => clearTimeout(timeout)
        }
    }, [isLinkCopied])

    /**
     * - Handles focus on popover opening.
     * - Resets copy link status when popover is closed.
     */
    useEffect(() => {
        if (isOpen) {
            if (isMobile && shareBoxMobile.current) {
                shareBoxMobile.current.focus()
            }

            if (!isMobile) {
                setTimeout(() => {
                    closeButtonDesktop.current?.focus()
                }, 50)
            }
        }

        if (!isOpen) {
            setTimeout(() => setIsLinkCopied(false), 200)
        }
    }, [isOpen, isMobile])

    return (
        <>
            {isOpen && <DarkOverlay onClick={onClose} />}

            {!isMobile ? (
                // anchorNearEdge: placement="bottom-end" + extra preventOverflow padding keeps a gap from
                // the viewport edge instead of hugging it flush, for triggers placed close to the window edge.
                // (default "clippingParents" boundary is kept: document.documentElement measures the full
                // scrollable content box, not the visible viewport, and produced bogus huge offsets)
                <Popover
                    isOpen={isOpen}
                    onClose={onClose}
                    initialFocusRef={closeButtonDesktop}
                    placement={anchorNearEdge ? 'bottom-end' : 'bottom'}
                    closeOnBlur={false}
                    modifiers={anchorNearEdge ? [{name: 'preventOverflow', options: {padding: 64}}] : undefined}
                >
                    <PopoverTrigger>{children}</PopoverTrigger>

                    <Portal>
                        <PopoverContent {...styles.content}>
                            <FocusLock persistentFocus={false}>
                                <PopoverBody {...styles.wrapper} data-testid="share-box">
                                    <HStack {...styles.header}>
                                        <Text as="h2" {...styles.title}>
                                            <FormattedMessage id="label.product.share" defaultMessage="Share" />
                                        </Text>
                                        <IconButton
                                            size="iconButtonSm"
                                            icon={<Icon name="IconClose" size="md" />}
                                            onClick={onClose}
                                            aria-label={formatMessage({id: 'alt.link.close', defaultMessage: 'close'})}
                                            ref={closeButtonDesktop}
                                        />
                                    </HStack>

                                    <List {...styles.socials} as={HStack}>
                                        {socialsShareData.map((socialName: ShareSocialName) => {
                                            const social = socialsShare.get(socialName)
                                            if (!social) return null

                                            if (socialName === 'wechat') {
                                                return (
                                                    <Popover key={socialName} trigger="hover" preventOverflow openDelay={0} closeDelay={0} placement={'bottom'}>
                                                        <PopoverTrigger>
                                                            <ListItem
                                                                as={Icon}
                                                                data-testid="wechat-icon"
                                                                aria-label={social.ariaLabel}
                                                                name={social.iconName}
                                                                size="lg"
                                                                cursor="pointer"
                                                                tabIndex={0}
                                                                role="listitem"
                                                            />
                                                        </PopoverTrigger>
                                                        <PopoverContent {...styles.weChatPopoverContent}>
                                                            <PopoverBody data-testid="wechat-popover" {...styles.weChatPopoverBody}>
                                                                <QRCode value={shareUrl} size={120} />
                                                            </PopoverBody>
                                                        </PopoverContent>
                                                    </Popover>
                                                )
                                            }

                                            return (
                                                <ListItem
                                                    key={socialName}
                                                    as={Link}
                                                    href={social.href}
                                                    aria-label={social.ariaLabel}
                                                    role="listitem"
                                                    onClick={(event) =>
                                                        handleSocialShare(
                                                            event as unknown as React.MouseEvent<HTMLAnchorElement>,
                                                            socialName,
                                                            social.socialAction,
                                                        )
                                                    }
                                                >
                                                    <Icon name={social.iconName} size="lg" />
                                                </ListItem>
                                            )
                                        })}
                                    </List>

                                    <HStack as="button" {...styles.shareButton} onClick={handleCopyLink}>
                                        <Icon name="IconLink" size="lg" />
                                        <Text {...styles.shareLabel} aria-live="polite" aria-atomic="true">
                                            {copyLinkLabel}
                                        </Text>
                                    </HStack>
                                </PopoverBody>
                            </FocusLock>
                        </PopoverContent>
                    </Portal>
                </Popover>
            ) : (
                <>
                    {children}

                    {isOpen && (
                        <Portal>
                            <Box ref={shareBoxMobile} tabIndex={0} data-testid="share-box-mobile" as={RemoveScroll} {...styles.mobileWrapper}>
                                <FocusLock persistentFocus={false}>
                                    <HStack {...styles.header}>
                                        <Text as="h2" {...styles.title}>
                                            <FormattedMessage id="label.product.share" defaultMessage="Share" />
                                        </Text>
                                        <IconButton
                                            size="iconButtonSm"
                                            icon={<Icon name="IconClose" size="md" />}
                                            onClick={onClose}
                                            aria-label={formatMessage({id: 'alt.link.close', defaultMessage: 'close'})}
                                            aria-hidden
                                        />
                                    </HStack>

                                    <List {...styles.mobileSocials} as={VStack}>
                                        <HStack as="button" {...styles.mobileSocial} cursor="pointer" role="listitem" onClick={handleCopyLink}>
                                            <Icon name="IconLink" size="xl" adjustXlScaling />
                                            <Text {...styles.mobileSocialName} aria-live="polite" aria-atomic="true">
                                                {copyLinkLabel}
                                            </Text>
                                        </HStack>

                                        {socialsShareData.map((socialName: ShareSocialName) => {
                                            const social = socialsShare.get(socialName)
                                            if (!social) return null

                                            return (
                                                <HStack
                                                    data-testid={`social-${socialName}`}
                                                    {...styles.mobileSocial}
                                                    as={Link}
                                                    key={socialName}
                                                    href={social.href}
                                                    aria-label={social.ariaLabel}
                                                    role="listitem"
                                                    tabIndex={0}
                                                    onKeyDown={(event) => event.key === 'Enter' && handleWeChatSocialClick()}
                                                    onClick={(event) =>
                                                        socialName === 'wechat'
                                                            ? handleWeChatSocialClick()
                                                            : handleSocialShare(
                                                                  event as unknown as React.MouseEvent<HTMLAnchorElement>,
                                                                  socialName,
                                                                  social.socialAction,
                                                              )
                                                    }
                                                >
                                                    {/* The slot stays the 32px `xl` box; only the glyph can be smaller (e.g. X at 25px), so the layout does not shift. */}
                                                    <Box boxSize="size.icon.xl" display="flex" alignItems="center" justifyContent="center">
                                                        <Icon name={social.iconName} size={social.mobileIconSize ?? 'xl'} adjustXlScaling />
                                                    </Box>
                                                    <Text {...styles.mobileSocialName}>{social.ariaLabel}</Text>
                                                </HStack>
                                            )
                                        })}
                                    </List>

                                    {showQRCode && (
                                        <Box data-testid="wechat-qrcode" {...styles.mobileWeChatOverlay} onClick={onQRCodeHide}>
                                            <Box {...styles.mobileWeChatImageWrapper}>
                                                <QRCode value={shareUrl} size={120} />
                                            </Box>
                                        </Box>
                                    )}
                                </FocusLock>
                            </Box>
                        </Portal>
                    )}
                </>
            )}
        </>
    )
}

export {ShareBox}
